import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Outlet, useNavigate } from 'react-router';
import { useSelector, useDispatch } from 'react-redux'
import Skeleton from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'
import Echo from 'laravel-echo';
import Pusher from 'pusher-js'; // eslint-disable-line no-unused-vars

import { SET_DELIVERY_DATA } from "store/delivery/actions"
import { SET_ACCOUNT } from "store/account/action"

import { httpOk, GET, closeWebSession } from 'helpers/api'
import { getSocketConfig as getEnvSocketConfig } from 'helpers/env'
import { clearTemporaryStorage } from 'helpers/helpers'

function socketRowId(row) {
	if (!row || typeof row !== 'object') return null
	return row.id ?? row.cheque_id ?? null
}

/** Массив чеков из payload (Laravel Echo: часто data — не массив, а объект или JSON-строка) */
function extractListFromSocketPayload(payload) {
	if (payload == null) return []
	if (Array.isArray(payload)) return payload

	let d = payload.data !== undefined ? payload.data : payload.message
	if (typeof d === 'string') {
		try {
			d = JSON.parse(d)
		} catch {
			return []
		}
	}

	if (Array.isArray(d)) return d
	if (d && typeof d === 'object') {
		if (Array.isArray(d.cheques)) return d.cheques
		if (Array.isArray(d.data)) return d.data
		if (Array.isArray(d.orders)) return d.orders
		if (Array.isArray(d.items)) return d.items
		if (socketRowId(d) != null) return [d]
	}

	if (typeof payload === 'object' && socketRowId(payload) != null) return [payload]
	return []
}

function extractIncomingCallFromSocketPayload(payload) {
	if (payload == null) return null
	if (typeof payload === 'string') {
		try {
			return JSON.parse(payload)
		} catch {
			return null
		}
	}

	if (payload.data !== undefined) {
		if (typeof payload.data === 'string') {
			try {
				return JSON.parse(payload.data)
			} catch {
				return null
			}
		}
		return payload.data
	}

	if (payload.message !== undefined) return payload.message
	return payload
}

/** Группы модификаторов в кэше без required/min/max — ответ старого бэкенда,
 * с ним колл-центр не открывает модалку обязательной группы. Тянем меню заново. */
function menuHasModifierLimits(nodes) {
	function checkProduct(product) {
		for (const group of product?.modifiers || []) {
			if (group?.required === undefined) return false
		}
		for (const size of product?.sizes || []) {
			if (!checkProduct(size)) return false
		}
		return true
	}

	function walk(list) {
		for (const node of list || []) {
			for (const product of node?.products || []) {
				if (!checkProduct(product)) return false
			}
			if (!walk(node?.children)) return false
		}
		return true
	}

	// Меню без групп модификаторов проверять нечем — считаем актуальным.
	return walk(nodes)
}

function Layout() {
	const dispatch = useDispatch()
	const navigate = useNavigate()
	const echoRef = useRef(null)
	const chequesEventDedupeAtRef = useRef(0)

	const reduxAccount = useSelector(state => state.account)
	const reduxDelivery = useSelector(state => state.delivery)

	const [isReady, setIsReady] = useState(!reduxDelivery?.poses?.length ? false : true);
	const [liveCheques, setLiveCheques] = useState(null)
	const [liveChequeDelta, setLiveChequeDelta] = useState(null)
	const [liveResyncAt, setLiveResyncAt] = useState(0)
	const [liveCourierLocations, setLiveCourierLocations] = useState({})
	const [liveStopList, setLiveStopList] = useState(null)
	const [livePresenceUsers, setLivePresenceUsers] = useState([])
	const [liveTelephony, setTelephonyPayload] = useState(null)

	async function getData() {
		try {
			let poses = reduxDelivery?.poses || [];
			let comments = reduxDelivery?.comments || [];
			let paymentTypes = reduxAccount?.call_center?.payment_types || [];

			// Настройки колл-центра приходят только в ответе на логин и живут в сохранённом
			// состоянии браузера. Если их там нет (состояние перезаписала другая вкладка
			// или оно не сохранилось) — дозапрашиваем, а не ждём повторного входа.
			// Права оператора (например F_PREORDER) берём свежие при каждом открытии: их
			// правят на ходу, и без этого выданное или снятое право ждало бы перезахода.
			// Аккаунт обновляем одним SET_ACCOUNT — два подряд затёрли бы друг друга
			const [permissionsResponse, settingsResponse] = await Promise.all([
				GET('/users/permissions', {}, { notification: false }),
				reduxAccount?.call_center ? null : GET("/delivery/call-center-settings"),
			]);
			const accountPatch = {};
			if (httpOk(permissionsResponse) && Array.isArray(permissionsResponse.data)) {
				accountPatch.permissions = permissionsResponse.data;
			}
			if (settingsResponse && httpOk(settingsResponse)) {
				accountPatch.call_center = settingsResponse.data;
				paymentTypes = settingsResponse.data?.payment_types || [];
			}
			if (Object.keys(accountPatch).length > 0) {
				dispatch(SET_ACCOUNT({ ...reduxAccount, ...accountPatch }));
			}
			let externalCouriers = reduxDelivery?.externalCouriers || [];
			let externalMenu = reduxDelivery?.externalMenu || [];
			let autoAddRules = reduxDelivery?.autoAddRules || [];
			let menuData = reduxDelivery?.menu || [];
			let products = reduxDelivery?.products || [];
			let discounts = reduxDelivery?.discounts || { by_pos: {} };

			if (!reduxDelivery?.poses?.length) {
				const response = await GET("/helper/poses");
				poses = response.data;
			}

			// Один раз в Redux; фильтр по order_type_sub_id на клиенте
			{
				const cached = reduxDelivery?.autoAddRules || []
				const needsRefresh = !cached.length
					|| cached.some((rule) => !Array.isArray(rule.order_type_sub_ids))
				if (needsRefresh) {
					const response = await GET("/delivery/auto-add-rules");
					if (httpOk(response)) autoAddRules = response.data;
				}
			}

			{
				const cached = reduxDelivery?.menu || []
				const needsRefresh = !cached.length || !menuHasModifierLimits(cached)
				if (needsRefresh) {
					const response = await GET("/delivery/menu");
					if (httpOk(response)) menuData = response.data.menu;
					if (httpOk(response)) products = response.data.products;
				}
			}

			// Обновляем и когда в кэше лежит ответ без is_default — иначе витрина
			// по умолчанию из настроек колл-центра не применится
			{
				const cached = reduxDelivery?.externalMenu || []
				const needsRefresh = !cached.length
					|| cached.some((item) => item.is_default === undefined)
				if (needsRefresh) {
					const response = await GET("/delivery/external-menu");
					if (httpOk(response)) externalMenu = response.data;
				}
			}

			if (!reduxDelivery?.comments?.length) {
				const response = await GET("/helper/comments");
				if (httpOk(response)) comments = response.data;
			}

			if (!reduxDelivery?.externalCouriers?.length) {
				const response = await GET("/helper/external-couriers");
				if (httpOk(response)) externalCouriers = response.data;
			}

			// Всегда обновляем: нужны order_type_sub_ids для точного match акций
			{
				const response = await GET("/v1/callcenter/discounts");
				if (httpOk(response)) discounts = response.data || { by_pos: {} };
			}

			dispatch(
				SET_DELIVERY_DATA({
					poses,
					menu: menuData,
					products,
					comments,
					paymentTypes,
					externalCouriers,
					externalMenu,
					autoAddRules,
					discounts,
				})
			);

			setIsReady(true);
		} catch (err) {
			console.error("Ошибка инициализации данных:", err);
		}
	}

	function getSocketConfig() {
		let wsHost;
		let wsPort;
		let authEndpoint;
		let isProd;

		if (process.env.NODE_ENV === 'production') {
			// В боевой сборке адрес приходит с сервера, а не из окружения фронта.
			wsHost = reduxAccount.configs.ws.host
			authEndpoint = reduxAccount.configs.ws.authEndpoint
			wsPort = 443
			isProd = true
		} else {
			// Локальный запуск: адрес зависит от того, в какой бэкенд целимся
			// (yarn start / yarn start:prod), иначе сокет уедет мимо API.
			const config = getEnvSocketConfig()
			wsHost = config.wsHost
			authEndpoint = config.authEndpoint
			wsPort = config.wsPort
			isProd = config.isProd
		}

		return { wsHost, wsPort, authEndpoint, isProd }
	}

	function connectSocket() {
		if (echoRef.current || !reduxAccount?.token || !reduxAccount?.parent || !reduxAccount?.configs?.ws?.broadcaster) {
			return
		}

		const socketConfig = getSocketConfig()
		try {
			echoRef.current = new Echo({
				broadcaster: reduxAccount.configs.ws.broadcaster,
				key: "mison_key",
				wsHost: socketConfig.wsHost,
				wsPort: socketConfig.wsPort,
				wssPort: socketConfig.wsPort,
				enabledTransports: ['ws', 'wss'],
				disableStats: true,
				encrypted: socketConfig.isProd,
				useTLS: socketConfig.isProd,
				forceTLS: socketConfig.isProd,
				authEndpoint: socketConfig.authEndpoint,
				auth: {
					headers: {
						'Authorization': `Bearer ${reduxAccount.token}`
					}
				},
			});
		} catch (error) {
			console.log(error)
			return
		}

		// Reverb доставку не гарантирует: за время обрыва дельты просто теряются.
		// Поэтому на каждое переподключение — заново тянем снапшот по HTTP.
		let didInitialConnect = false
		echoRef.current.connector.pusher.connection.bind('connected', () => {
			if (didInitialConnect) {
				setLiveResyncAt(Date.now())
			}
			didInitialConnect = true
		});

		echoRef.current.connector.pusher.connection.bind('disconnected', () => {
			console.log('disconnected private channel');
		});

		// Канал бренда: имя приходит с сервера в configs.ws.channels, а не строится здесь.
		// Колл-центр — единственный клиент бренд-канала, который не живёт в сторе, поэтому
		// он переезжает на новое имя первым: пока он на legacy, старый канал нельзя убрать
		// даже после обновления курьера.
		//
		// LEGACY-SOCKETS: фолбэк нужен, если бэкенд окажется старым и не пришлёт channels
		// (например, при откате). Удалить вместе с ключом brand_legacy на сервере —
		// тогда же станет мёртвым и слушатель Cheques ниже: полный список шлётся
		// ТОЛЬКО в legacy-канал, в новый он намеренно не дублируется.
		const brandChannel = reduxAccount?.configs?.ws?.channels?.brand
			?? `call-center.${reduxAccount.parent}`

		const privateChannel = echoRef.current.private(brandChannel)

		const applyChequesFromSocket = (payload) => {
			const now = Date.now()
			if (now - chequesEventDedupeAtRef.current < 80) return
			chequesEventDedupeAtRef.current = now

			console.log('payload: ', payload?.data)
			const list = extractListFromSocketPayload(payload)
			if (!list.length) {
				console.warn('delivery Cheques: не удалось извлечь массив из payload, см. структуру выше')
				return
			}
			setLiveCheques([...list])
		}

		privateChannel.listen('Cheques', applyChequesFromSocket)
		privateChannel.listen('.Cheques', applyChequesFromSocket)

		// Сокеты v2: дельта одной строки вместо всего списка.
		// Старое событие Cheques пока живёт рядом — оно нужно курьеру, пока тот
		// не обновится в сторе. Оба потока безвредны: применяем то, что пришло.
		const applyChequeDelta = (payload) => {
			if (!payload || !payload.data) return
			setLiveChequeDelta({
				action: payload.action,
				visible: payload.visible !== false,
				revision: Number(payload.revision) || 0,
				row: payload.data,
				receivedAt: Date.now(),
			})
		}

		privateChannel.listen('.cheque.created', applyChequeDelta)
		privateChannel.listen('.cheque.updated', applyChequeDelta)
		privateChannel.listen('.cheque.closed', applyChequeDelta)
		privateChannel.listen('.cheque.deleted', applyChequeDelta)

		// Позиции курьеров. Онлайн считаем по свежести updated_at, а не по факту
		// сокета: курьер в лифте и метро рвёт соединение постоянно, и presence
		// давал бы шторм «ушёл — пришёл» вместо «был две минуты назад».
		privateChannel.listen('.courier.location', (payload) => {
			if (!payload || payload.user_id == null) return
			setLiveCourierLocations((prev) => ({
				...prev,
				[payload.user_id]: {
					user_id: payload.user_id,
					latitude: payload.latitude,
					longitude: payload.longitude,
					updated_at: payload.updated_at,
					receivedAt: Date.now(),
				},
			}))
		})

		privateChannel.listen('StopList', (payload) => {
			const list = extractListFromSocketPayload(payload)
			setLiveStopList(list.length ? [...list] : [])
		})

		const applyIncomingCallFromSocket = (payload) => {
			const telephonyPayload = extractIncomingCallFromSocketPayload(payload)
			console.log('telephonyPayload', telephonyPayload)
			if (!telephonyPayload) return
			setTelephonyPayload({
				...telephonyPayload,
				receivedAt: Date.now(),
			})
		}

		privateChannel.listen('CallAccepted', applyIncomingCallFromSocket)
		privateChannel.listen('.CallAccepted', applyIncomingCallFromSocket)

		// Онлайн точек. Из этого канала читается ровно одно — есть ли участник
		// с нужным pos_id; ни логин, ни телефон сотрудника здесь не нужны.
		//
		// Подписываемся на ОБА канала: новый `brand-online.{hmac}`, где участник —
		// это точка, и legacy `presence-call-center-{login}`, где участник — человек.
		// Пока часть касс на старых сборках, полную картину даёт только объединение.
		//
		// LEGACY-SOCKETS: когда все кассы обновятся, убрать joinPresence(legacy...)
		// и разбор старого формата в toPresenceEntry.
		const channels = reduxAccount?.configs?.ws?.channels

		// Ключ с префиксом канала: id участника в новом канале — это terminals.id,
		// в legacy — users.id, и нумеруются они независимо. Без префикса терминал №5
		// и пользователь №5 схлопнулись бы в одну запись при слиянии двух списков.
		const toPresenceEntry = (member, isLegacy) => {
			if (!member) return null
			// member_id есть только в новом канале; в legacy участник опознаётся по id
			const rawKey = member.member_id ?? member.id
			if (rawKey == null) return null

			return { 'key': `${isLegacy ? 'l' : 'n'}:${rawKey}`, 'pos_id': member.pos_id ?? null }
		}

		const joinPresence = (channelName, isLegacy) => {
			if (!channelName) return

			echoRef.current.join(channelName)
				.here((members) => {
					const entries = (members || [])
						// в legacy колл-центр приходит человеком и его надо отфильтровать;
						// в новом канале он приходит с pos_id = null и отсеется сам
						.filter((item) => !isLegacy || item?.role !== 'ROLE_CALL_CENTER')
						.map((item) => toPresenceEntry(item, isLegacy))
						.filter(Boolean)

					setLivePresenceUsers((prev) => [
						...prev.filter((item) => !entries.some((entry) => entry.key === item.key)),
						...entries,
					])
				})
				.joining((member) => {
					if (isLegacy && member?.role === 'ROLE_CALL_CENTER') return

					const entry = toPresenceEntry(member, isLegacy)
					if (!entry) return

					setLivePresenceUsers((prev) => (
						prev.some((item) => item.key === entry.key) ? prev : [...prev, entry]
					))
				})
				.leaving((member) => {
					const entry = toPresenceEntry(member, isLegacy)
					if (!entry) return

					setLivePresenceUsers((prev) => prev.filter((item) => item.key !== entry.key))
				})
		}

		joinPresence(channels?.brand_presence, false)
		joinPresence(channels?.brand_presence_legacy ?? `presence-call-center-${reduxAccount.parent}`, true)
	}

	useEffect(() => {
		getData();
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	function disconnectSocket() {
		try {
			echoRef?.current?.disconnect()
		} catch (error) {
			console.log(error)
		}
		echoRef.current = null
	}

	useEffect(() => {
		connectSocket()
		return () => {
			disconnectSocket()
		}
	}, [reduxAccount?.token, reduxAccount?.parent, reduxAccount?.configs?.ws?.broadcaster]) // eslint-disable-line react-hooks/exhaustive-deps

	const logout = useCallback(async () => {
		disconnectSocket()
		await closeWebSession()
		dispatch({ type: 'USER_LOGGED_OUT', payload: null })
		clearTemporaryStorage()
		navigate('/auth/login')
	}, [dispatch, navigate])

	const outletContext = useMemo(() => ({
		liveCheques,
		liveChequeDelta,
		liveResyncAt,
		liveCourierLocations,
		liveStopList,
		livePresenceUsers,
		liveTelephony,
		logout,
	}), [liveCheques, liveChequeDelta, liveResyncAt, liveCourierLocations, liveStopList, livePresenceUsers, liveTelephony, logout])

	if (!isReady) {
		return (
			<>
				<div style={{
					height: '100vh',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center'
				}}>
					<Skeleton height={100} width={300} borderRadius={12} />
				</div>

				<div style={{
					'position': 'absolute',
					'top': '50%',
					'left': '50%',
					'transform': 'translate(-50%, -50%)',
					'zIndex': 1,
					'background': 'var(--dark-surface, #fff)',
					'padding': '20px',
					'borderRadius': '12px',
					'boxShadow': '0 2px 8px rgba(0,0,0,0.15)',
				}}>
					Загрузка данных колл-центра...
				</div>
			</>
		);
	}

	return (
		<div>
			<Outlet context={outletContext} />
		</div>
	);
}

export default Layout
